import { useState, useEffect } from 'react';

import { CRUISE_PAST_SEARCH } from '@constants/constants';
import { SearchViewModel, WebUrlType } from '@api/v3';
import { callService } from '@api/remote-call';
import { ship_ga4Event } from '@utils/ga4Event/ship_ga4Event';
import { handleFilterAndUpdateUrl } from '@utils/utils';
import clean from '@utils/cleanObject';
import {
  getSafeLocalStorageItem,
  removeSafeLocalStorageItem,
  setSafeLocalStorageItem,
} from '@utils/localStorage/getSafeLocalStorage';
import { useAppRouter } from '@contexts/router';

import { BookerSearchDrawer } from './common/BookerSearchDrawer';
import { BookerSearchViewModel } from './common/BookerSearchViewModel';
import { useGetPopularSearches } from './hooks';
import { WhereToGoSearchContainer } from './styled';

export interface CruiseBookerError {
  location?: boolean;
}
interface CruiseTabContentProps {
  subtitle: string;
  onHrefUpdate?: (href: string) => void;
  onSearchCalled: () => void;
  href: string;
  type: string;
  setError: React.Dispatch<
    React.SetStateAction<Partial<CruiseBookerError | undefined>>
  >;
  setInitialError: React.Dispatch<
    React.SetStateAction<Partial<CruiseBookerError | undefined>>
  >;
  error?: CruiseBookerError;
  initialError?: CruiseBookerError;
}
export const CruiseTabContent = ({
  subtitle,
  onHrefUpdate,
  href,
  onSearchCalled,
  type,
  error,
  setError,
  setInitialError,
  initialError,
}: CruiseTabContentProps) => {
  const { items: popularSearchItems } = useGetPopularSearches({
    type: 'cruise',
  });
  const router = useAppRouter();
  const [pastCruiseItems, setPastCruiseItems] = useState<
    BookerSearchViewModel[]
  >(
    getSafeLocalStorageItem(CRUISE_PAST_SEARCH)
      ? JSON.parse(getSafeLocalStorageItem(CRUISE_PAST_SEARCH) || '[]')
      : []
  );
  const [searchedItemText, setSearchedItemText] = useState('');

  const clearPastCruiseSearch = () => {
    removeSafeLocalStorageItem(CRUISE_PAST_SEARCH);
    setPastCruiseItems([]);
  };

  const removeItemFromLocalStorage = (item: BookerSearchViewModel) => {
    const newItems = pastCruiseItems.filter(
      (i: BookerSearchViewModel) => i.text !== item.text
    );

    setPastCruiseItems(newItems);
    setSafeLocalStorageItem(CRUISE_PAST_SEARCH, JSON.stringify(newItems));
  };

  // ===========================================
  // Update the next search base on the popular search item
  // ===========================================
  const handlePopularItemSelected = (item: SearchViewModel) => {
    onHrefUpdate?.(
      handleFilterAndUpdateUrl(
        clean({
          slug: item?.url,
        })
      )
    );
  };

  // ===========================================
  // Handle save items to local storage
  // ===========================================

  const saveItems = async () => {
    if (!href || !type) return;

    let pastCruiseItems: BookerSearchViewModel[] = [];
    let cruiseItems: BookerSearchViewModel[] = [];
    switch (type) {
      case WebUrlType.CRUISE_PORT:
      case WebUrlType.CRUISE_THEME:
      case WebUrlType.CRUISE_REGION:
      case WebUrlType.CRUISE_CATEGORY:
      case WebUrlType.CRUISE:
        if (getSafeLocalStorageItem(CRUISE_PAST_SEARCH)) {
          pastCruiseItems = JSON.parse(
            getSafeLocalStorageItem(CRUISE_PAST_SEARCH) || '[]'
          );
        }

        cruiseItems = pastCruiseItems ? [...pastCruiseItems] : [];

        if (!cruiseItems.find((i) => i.text === href)) {
          cruiseItems.unshift({
            id: Math.random(),
            text: searchedItemText,
            url: href,
            type: type,
          });
        } else {
          const target = cruiseItems.find((i) => i.url === href);

          if (target) {
            const targetIndex = cruiseItems.indexOf(target);
            cruiseItems.splice(targetIndex, 1);
          }
          cruiseItems.unshift({
            id: Math.random(),
            text: searchedItemText,
            url: href,
            type: type,
          });
        }
        setSafeLocalStorageItem(
          CRUISE_PAST_SEARCH,
          JSON.stringify(cruiseItems)
        );
        router.push(href);
        break;

      default:
        router.push(href);
    }
  };
  useEffect(() => {
    if (!href || !type) return;
    saveItems();

    const cruiseGa4EventData = {
      destination_point: searchedItemText,
    };
    ship_ga4Event(cruiseGa4EventData);
  }, [onSearchCalled]);

  useEffect(() => {
    if (!href.split('?')[0]) {
      setInitialError((val) => ({
        ...val,
        location: true,
      }));
    } else {
      setInitialError((val) => ({
        ...val,
        location: false,
      }));

      setError({
        ...initialError,
        location: false,
      });
    }
  }, [href, location]);

  return (
    <>
      <WhereToGoSearchContainer>
        <BookerSearchDrawer
          type='Cruise'
          onHrefUpdate={onHrefUpdate}
          placeholder={subtitle}
          onSearchItemTextUpdate={setSearchedItemText}
          responsiveTitle={subtitle}
          fetchItems={(param: string) => {
            return callService(
              {
                service: 'SearchesService',
                method: 'searchesCruise',
                version: 'v3',
              },
              {
                s: param || '',
              }
            );
          }}
          history={pastCruiseItems?.slice(0, 3)}
          onClearClick={clearPastCruiseSearch}
          onRemoveItem={removeItemFromLocalStorage}
          error={error?.location}
          popularSearchItems={popularSearchItems}
          onPopularSearchItemSelected={handlePopularItemSelected}
        />
      </WhereToGoSearchContainer>
    </>
  );
};
export default CruiseTabContent;
